const AUDIO_SETTINGS_KEY = 'incoprea.shardmeadow.audio.v1';
const TRACKS = Object.freeze({
  explore: './audio/shardmeadow-exploration-v1.mp3',
  battle: './audio/shardmeadow-battle-v1.mp3',
});

function loadSettings() {
  try {
    const saved = JSON.parse(localStorage.getItem(AUDIO_SETTINGS_KEY) || '{}');
    return { music: saved.music !== false, sfx: saved.sfx !== false };
  } catch { return { music: true, sfx: true }; }
}

function teamFor(state, factionId) {
  return state?.factions?.find(item => item.id === factionId)?.teamId || null;
}

function activeCombat(state) {
  if (!state || state.paused || state.outcome) return false;
  const alliedTeam = state.playerTeamId;
  return state.units.some(unit => unit.hp > 0 && teamFor(state, unit.faction) === alliedTeam
    && ['attacking', 'engaging', 'pursuing', 'charging', 'stampeding'].includes(unit.activity));
}

export function createAudioDirector() {
  const settings = loadSettings();
  const tracks = Object.fromEntries(Object.entries(TRACKS).map(([key, src]) => {
    const audio = new Audio(src);
    audio.loop = true;
    audio.preload = 'metadata';
    audio.volume = 0;
    return [key, audio];
  }));
  let context = null;
  let unlocked = false;
  let mode = 'explore';
  let suspended = false;
  let lastUpdate = performance.now();
  let lastEventId = null;
  let lastImpactAt = 0;

  function saveSettings() {
    try { localStorage.setItem(AUDIO_SETTINGS_KEY, JSON.stringify(settings)); } catch {}
  }

  async function unlock() {
    try {
      context ||= new AudioContext();
      await context.resume();
      if (!unlocked) {
        unlocked = true;
        for (const track of Object.values(tracks)) track.play().catch(() => {});
      }
    } catch {}
  }

  function targetVolume(key) {
    if (!settings.music || !unlocked || suspended) return 0;
    return key === mode ? (key === 'battle' ? .36 : .29) : 0;
  }

  function updateVolumes(now = performance.now()) {
    const delta = Math.min(.25, Math.max(.016, (now - lastUpdate) / 1000));
    lastUpdate = now;
    const blend = Math.min(1, delta * 1.6);
    for (const [key, track] of Object.entries(tracks)) {
      const target = targetVolume(key);
      track.volume += (target - track.volume) * blend;
      if (unlocked && !track.paused && track.volume < .001 && target === 0 && suspended) track.pause();
      else if (unlocked && track.paused && target > 0) track.play().catch(() => {});
    }
  }

  function setSuspended(value) {
    suspended = Boolean(value);
    if (!suspended && unlocked) for (const track of Object.values(tracks)) track.play().catch(() => {});
    updateVolumes();
  }

  function voice(frequency, duration, gain, type = 'triangle', delay = 0) {
    if (!context || !settings.sfx || suspended) return;
    const at = context.currentTime + delay;
    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, at);
    envelope.gain.setValueAtTime(.0001, at);
    envelope.gain.exponentialRampToValueAtTime(gain, at + .008);
    envelope.gain.exponentialRampToValueAtTime(.0001, at + duration);
    oscillator.connect(envelope).connect(context.destination);
    oscillator.start(at);
    oscillator.stop(at + duration + .02);
  }

  function noise(duration = .06, gain = .018) {
    if (!context || !settings.sfx || suspended) return;
    const frames = Math.max(1, Math.floor(context.sampleRate * duration));
    const buffer = context.createBuffer(1, frames, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let index = 0; index < frames; index++) data[index] = (Math.random() * 2 - 1) * (1 - index / frames);
    const source = context.createBufferSource(), filter = context.createBiquadFilter(), envelope = context.createGain();
    filter.type = 'lowpass'; filter.frequency.value = 920;
    envelope.gain.setValueAtTime(gain, context.currentTime);
    envelope.gain.exponentialRampToValueAtTime(.0001, context.currentTime + duration);
    source.buffer = buffer; source.connect(filter).connect(envelope).connect(context.destination); source.start();
  }

  function ui(frequency = 260, duration = .05) {
    unlock();
    voice(frequency, duration, .026, 'triangle');
    voice(frequency * 1.5, duration * .72, .012, 'sine', .014);
  }

  function cue(kind) {
    if (!settings.sfx || !context || suspended) return;
    if (kind === 'impact') { noise(.065, .016); voice(92, .09, .014, 'sine'); return; }
    if (kind === 'construction') { voice(196, .18, .02); voice(294, .2, .015, 'triangle', .09); return; }
    if (kind === 'discovery') { voice(392, .24, .016); voice(587, .28, .012, 'sine', .12); return; }
    if (kind === 'triumph') { [330, 440, 660].forEach((note, index) => voice(note, .34, .014, 'triangle', index * .09)); return; }
    if (kind === 'loss') { voice(180, .35, .018, 'sawtooth'); voice(120, .45, .012, 'sine', .1); }
  }

  function syncEvents(state) {
    const events = state?.events || [];
    if (!events.length) return;
    if (lastEventId == null) { lastEventId = events.at(-1)?.id || null; return; }
    const start = Math.max(0, events.findIndex(event => event.id === lastEventId) + 1);
    const fresh = events.slice(start);
    lastEventId = events.at(-1)?.id || lastEventId;
    for (const event of fresh) {
      const ownTeamEvent = event.teamId === state.playerTeamId || teamFor(state, event.faction) === state.playerTeamId;
      if ((event.type === 'hit' || event.type === 'defensive-shot') && ownTeamEvent) {
        const now = performance.now(); if (now - lastImpactAt > 145) { cue('impact'); lastImpactAt = now; }
      } else if (ownTeamEvent && ['construction-complete', 'research-complete', 'fortification-upgraded', 'unit-trained'].includes(event.type)) cue('construction');
      else if (ownTeamEvent && ['camp-discovered', 'treasure-collected', 'well-captured'].includes(event.type)) cue('discovery');
      else if (ownTeamEvent && ['age-up', 'hero-recruited', 'hero-level-up', 'team-victory'].includes(event.type)) cue('triumph');
      else if (ownTeamEvent && ['unit-lost', 'faction-defeated', 'team-defeated'].includes(event.type)) cue('loss');
    }
  }

  function update(state) {
    mode = activeCombat(state) ? 'battle' : 'explore';
    syncEvents(state);
    updateVolumes();
  }

  function toggle(kind) {
    if (!(kind in settings)) return;
    settings[kind] = !settings[kind];
    saveSettings();
    unlock();
    updateVolumes();
  }

  function resetEvents(state) { lastEventId = state?.events?.at(-1)?.id || null; }

  return { settings, unlock, ui, update, toggle, setSuspended, resetEvents };
}
